import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, TextField, Button, IconButton,
  Select, MenuItem, FormControl, InputLabel, Chip, Alert, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Switch, FormControlLabel } from '@mui/material';
import { Add, Delete, Save, ArrowUpward, ArrowDownward, BarChart as BarIcon,
  Visibility } from '@mui/icons-material';
import { dashboardsAPI, formsAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';
import { v4 as uuidv4 } from '../../utils/uuid';
import { renderChart } from '../../components/Charts';

var CHART_TYPES = [
  { value: 'bar',    label: 'Horizontal bars \u2014 good for long answers' },
  { value: 'column', label: 'Vertical bars \u2014 good for short or ordered answers' },
  { value: 'pie',    label: 'Donut \u2014 good for a handful of options' },
  { value: 'line',   label: 'Line \u2014 good for dates or ordered scales' },
  { value: 'table',  label: 'Table \u2014 exact counts' },
  { value: 'stat',   label: 'Single number \u2014 total answers' },
];

export default function Dashboards() {
  var sn = useSnackbar(); var notify = sn.enqueueSnackbar;
  var ls = useState([]); var list = ls[0]; var setList = ls[1];
  var fs = useState([]); var forms = fs[0]; var setForms = fs[1];
  var es = useState(null); var editing = es[0]; var setEditing = es[1];
  var sc = useState(null); var schema = sc[0]; var setSchema = sc[1];
  var pv = useState(null); var preview = pv[0]; var setPreview = pv[1];

  function load() {
    dashboardsAPI.list().then(function(r) { setList(r.data.data || []); });
  }

  useEffect(function() {
    load();
    formsAPI.list().then(function(r) { setForms(r.data.data || []); });
  }, []);

  // The field list comes from whichever form the dashboard is built against.
  useEffect(function() {
    if (!editing || !editing.form_id) { setSchema(null); return; }
    formsAPI.get(editing.form_id).then(function(r) {
      var s = r.data.data.schema;
      var parsed = typeof s === 'string' ? JSON.parse(s) : s;
      if (typeof parsed === 'string') { try { parsed = JSON.parse(parsed); } catch (e) { parsed = {}; } }
      setSchema(parsed);
    });
  }, [editing && editing.form_id]);

  function startNew() {
    setEditing({ id: '', form_id: '', title: '', description: '', charts: [], is_active: true });
  }

  function startEdit(d) {
    var charts = d.charts;
    if (typeof charts === 'string') { try { charts = JSON.parse(charts); } catch (e) { charts = []; } }
    setEditing({ id: d.id, form_id: d.form_id, title: d.title, description: d.description || '',
      charts: charts || [], is_active: d.is_active });
  }

  function setE(key, value) {
    var o = Object.assign({}, editing); o[key] = value; setEditing(o);
  }

  function addChart() {
    setE('charts', (editing.charts || []).concat([
      { id: uuidv4(), title: '', type: 'bar', field_id: '', group_by: '', limit: 12 }]));
  }
  function updateChart(i, key, value) {
    var c = (editing.charts || []).slice();
    c[i] = Object.assign({}, c[i]); c[i][key] = value;
    setE('charts', c);
  }
  function moveChart(i, delta) {
    var c = (editing.charts || []).slice();
    var j = i + delta;
    if (j < 0 || j >= c.length) return;
    var t = c[i]; c[i] = c[j]; c[j] = t;
    setE('charts', c);
  }
  function removeChart(i) {
    setE('charts', (editing.charts || []).filter(function(_, k){ return k !== i; }));
  }

  function save() {
    if (!editing.form_id || !editing.title.trim()) {
      notify('Pick a form and give the dashboard a name', {variant:'warning'});
      return;
    }
    dashboardsAPI.save(editing).then(function() {
      notify('Dashboard saved', {variant:'success'});
      setEditing(null); load();
    }).catch(function(e) {
      notify((e.response && e.response.data && e.response.data.error) || 'Failed to save', {variant:'error'});
    });
  }

  function remove(d) {
    if (!window.confirm('Delete the dashboard "' + d.title + '"? Charts are configuration only \u2014 no application data is affected.')) return;
    dashboardsAPI.remove(d.id).then(function() { notify('Deleted', {variant:'info'}); load(); });
  }

  function openPreview(d) {
    dashboardsAPI.data(d.id).then(function(r) { setPreview(r.data.data); })
      .catch(function() { notify('Could not load preview', {variant:'error'}); });
  }

  var fields = schema ? (schema.fields || []).slice().sort(function(a,b){ return (a.order||0)-(b.order||0); }) : [];
  var sections = schema ? (schema.sections || []) : [];
  function sectionTitle(id) {
    var s = sections.filter(function(x){ return x.id === id; })[0];
    return s ? s.title : '';
  }

  var E = React.createElement;

  return E(Box, null,
    E(Box, {sx:{display:'flex', justifyContent:'space-between', alignItems:'center', mb:1}},
      E(Typography, {variant:'h4'}, 'Dashboards'),
      E(Button, {variant:'contained', startIcon:E(Add), onClick:startNew}, 'New dashboard')),
    E(Typography, {variant:'body2', color:'text.secondary', sx:{mb:3}},
      'Charts built from the answers to a form. Viewer accounts see the dashboards for the forms they have been given access to.'),

    E(Card, null,
      E(TableContainer, null,
        E(Table, {size:'small'},
          E(TableHead, null,
            E(TableRow, null,
              E(TableCell, null, 'Dashboard'),
              E(TableCell, null, 'Form'),
              E(TableCell, {align:'right'}, 'Charts'),
              E(TableCell, null, 'Active'),
              E(TableCell, {align:'right'}, 'Actions'))),
          E(TableBody, null,
            list.map(function(d) {
              var charts = d.charts;
              if (typeof charts === 'string') { try { charts = JSON.parse(charts); } catch (e) { charts = []; } }
              return E(TableRow, {key:d.id, hover:true},
                E(TableCell, null, d.title),
                E(TableCell, null, d.form_title),
                E(TableCell, {align:'right'}, (charts || []).length),
                E(TableCell, null, E(Chip, {size:'small', label:d.is_active ? 'Active' : 'Hidden',
                  color:d.is_active ? 'success' : 'default', variant:d.is_active ? 'filled' : 'outlined'})),
                E(TableCell, {align:'right'},
                  E(Button, {size:'small', startIcon:E(Visibility),
                    onClick:function(){ openPreview(d); }}, 'Preview'),
                  E(Button, {size:'small', onClick:function(){ startEdit(d); }}, 'Edit'),
                  E(IconButton, {size:'small', color:'error', onClick:function(){ remove(d); }},
                    E(Delete, {fontSize:'small'}))));
            }),
            list.length === 0
              ? E(TableRow, null, E(TableCell, {colSpan:5, align:'center',
                  sx:{py:4, color:'text.secondary'}}, 'No dashboards yet'))
              : null)))),

    // ── Editor ─────────────────────────────────────────────
    editing ? E(Dialog, {open:true, onClose:function(){ setEditing(null); }, maxWidth:'lg', fullWidth:true},
      E(DialogTitle, null, editing.id ? 'Edit dashboard' : 'New dashboard'),
      E(DialogContent, {dividers:true},
        E(Grid, {container:true, spacing:2, sx:{mb:2}},
          E(Grid, {item:true, xs:12, md:4},
            E(FormControl, {fullWidth:true, size:'small'},
              E(InputLabel, null, 'Form'),
              E(Select, {value:editing.form_id, label:'Form',
                onChange:function(e){ setE('form_id', e.target.value); }},
                forms.map(function(f) { return E(MenuItem, {key:f.id, value:f.id}, f.title); })))),
          E(Grid, {item:true, xs:12, md:4},
            E(TextField, {fullWidth:true, size:'small', label:'Dashboard name', value:editing.title,
              onChange:function(e){ setE('title', e.target.value); }})),
          E(Grid, {item:true, xs:12, md:3},
            E(TextField, {fullWidth:true, size:'small', label:'Description (optional)',
              value:editing.description,
              onChange:function(e){ setE('description', e.target.value); }})),
          E(Grid, {item:true, xs:12, md:1},
            E(FormControlLabel, {
              control:E(Switch, {checked:!!editing.is_active,
                onChange:function(e){ setE('is_active', e.target.checked); }}),
              label:'Active'}))),

        E(Divider, {sx:{mb:2}}),

        !editing.form_id
          ? E(Alert, {severity:'info'}, 'Pick a form first \u2014 its questions become the chart options.')
          : E(Box, null,
              (editing.charts || []).map(function(c, i) {
                return E(Card, {key:c.id || i, variant:'outlined', sx:{mb:1.5}},
                  E(CardContent, {sx:{py:1.5}},
                    E(Grid, {container:true, spacing:1.5, alignItems:'center'},
                      E(Grid, {item:true, xs:12, md:3},
                        E(TextField, {fullWidth:true, size:'small', label:'Chart title',
                          value:c.title || '', placeholder:'Applicants by country',
                          onChange:function(e){ updateChart(i, 'title', e.target.value); }})),
                      E(Grid, {item:true, xs:12, md:3},
                        E(FormControl, {fullWidth:true, size:'small'},
                          E(InputLabel, null, 'Question'),
                          E(Select, {value:c.field_id || '', label:'Question',
                            onChange:function(e){ updateChart(i, 'field_id', e.target.value); }},
                            fields.map(function(f) {
                              var st = sectionTitle(f.section_id);
                              return E(MenuItem, {key:f.id, value:f.id},
                                f.label + (st ? '  \u2014 ' + st : ''));
                            })))),
                      E(Grid, {item:true, xs:12, md:2.5},
                        E(FormControl, {fullWidth:true, size:'small'},
                          E(InputLabel, null, 'Chart type'),
                          E(Select, {value:c.type || 'bar', label:'Chart type',
                            onChange:function(e){ updateChart(i, 'type', e.target.value); }},
                            CHART_TYPES.map(function(t) {
                              return E(MenuItem, {key:t.value, value:t.value}, t.label);
                            })))),
                      E(Grid, {item:true, xs:12, md:2.5},
                        E(FormControl, {fullWidth:true, size:'small'},
                          E(InputLabel, null, 'Split by (optional)'),
                          E(Select, {value:c.group_by || '', label:'Split by (optional)',
                            onChange:function(e){ updateChart(i, 'group_by', e.target.value); }},
                            E(MenuItem, {value:''}, E('em', null, 'No split')),
                            fields.map(function(f) {
                              return E(MenuItem, {key:f.id, value:f.id}, f.label);
                            })))),
                      E(Grid, {item:true, xs:6, md:0.5},
                        E(TextField, {fullWidth:true, size:'small', type:'number', label:'Top',
                          value:c.limit || 12,
                          onChange:function(e){ updateChart(i, 'limit', parseInt(e.target.value, 10) || 12); }})),
                      E(Grid, {item:true, xs:6, md:0.5, sx:{display:'flex'}},
                        E(IconButton, {size:'small', onClick:function(){ moveChart(i, -1); }},
                          E(ArrowUpward, {fontSize:'inherit'})),
                        E(IconButton, {size:'small', onClick:function(){ moveChart(i, 1); }},
                          E(ArrowDownward, {fontSize:'inherit'})),
                        E(IconButton, {size:'small', color:'error', onClick:function(){ removeChart(i); }},
                          E(Delete, {fontSize:'inherit'}))))));
              }),
              E(Button, {size:'small', startIcon:E(BarIcon), onClick:addChart}, 'Add chart'),
              (editing.charts || []).length === 0
                ? E(Alert, {severity:'info', sx:{mt:2}},
                    'No charts yet. Each chart counts the answers to one question, optionally split by a second one.')
                : null,
              E(Alert, {severity:'info', sx:{mt:2}},
                'Counts cover live applications only \u2014 discarded ones are excluded. Questions that allow several answers count once per selected option, so totals can exceed the number of applications.'))),

      E(DialogActions, null,
        E(Button, {onClick:function(){ setEditing(null); }}, 'Cancel'),
        E(Button, {variant:'contained', startIcon:E(Save), onClick:save}, 'Save dashboard'))) : null,

    // ── Preview ────────────────────────────────────────────
    preview ? E(Dialog, {open:true, onClose:function(){ setPreview(null); }, maxWidth:'lg', fullWidth:true},
      E(DialogTitle, null, preview.title + ' \u2014 preview'),
      E(DialogContent, {dividers:true},
        E(Chip, {label:preview.applications + ' applications', size:'small', sx:{mb:2}}),
        E(Grid, {container:true, spacing:2},
          (preview.charts || []).map(function(c) {
            var wide = c.type === 'table' || c.type === 'line' || c.type === 'column';
            return E(Grid, {item:true, xs:12, md: wide ? 12 : 6, key:c.id},
              E(Card, {variant:'outlined', sx:{height:'100%'}},
                E(CardContent, null,
                  E(Typography, {variant:'subtitle1', fontWeight:600, sx:{mb:1.5}},
                    c.title || 'Untitled'),
                  c.error ? E(Alert, {severity:'warning'}, c.error) : renderChart(c))));
          }))),
      E(DialogActions, null,
        E(Button, {onClick:function(){ setPreview(null); }}, 'Close'))) : null);
}
